import { createContext, useContext, useState, ReactNode, Dispatch, SetStateAction } from "react";
import { MRT_PaginationState, MRT_SortingState } from "material-react-table";
import { useAllUsers, User } from "src/api/client-api/user/useAllUsers";
import { useDelayedSearch } from "src/utils/useDelayedSearch";
import { useGridColumns, GridColumnsType } from "src/components/Grid/hooks/columnsExtension/useGridColumns.tsx";
import { useUserColumns } from "./useUserColumns.tsx";

type UserListContextInitialValues = {
    userPage: MRT_PaginationState;
    setUserPage: Dispatch<SetStateAction<MRT_PaginationState>>;

    sorting: MRT_SortingState;
    setSorting: Dispatch<SetStateAction<MRT_SortingState>>;

    totalItems: number | undefined;

    users: User[] | undefined;
    mrt_columns: GridColumnsType<User>;
    isLoading: boolean;

    searchUser: string;
    delayedSearchUser: string;
    setSearchUser: (arg1: string) => void;
    resetSearch: () => void;

    // Modal states
    showCreateModal: boolean;
    setShowCreateModal: Dispatch<SetStateAction<boolean>>;

    gridKey: string;
};

const UserListContext = createContext<UserListContextInitialValues | undefined>(undefined);

type UserListContextProviderProps = {
    children: ReactNode;
};

export const UserListContextProvider = (props: UserListContextProviderProps) => {
    const { children } = props;

    const gridKey = "settings-users-grid";

    const [userPage, setUserPage] = useState<MRT_PaginationState>({
        pageIndex: 0,
        pageSize: 20,
    });
    const [sorting, setSorting] = useState<MRT_SortingState>([{ id: "lastname", desc: false }]);

    // Modal states
    const [showCreateModal, setShowCreateModal] = useState(false);

    const search = useDelayedSearch();
    const { users, isLoading, totalItems } = useAllUsers({
        page: userPage.pageIndex + 1,
        itemsPerPage: userPage.pageSize,
        search: search.delayedSearch,
        email: "",
        companyName: "",
        sorting: sorting,
    });

    const mrt_userColumns = useUserColumns({ users });
    const mrt_columns = useGridColumns<User>(mrt_userColumns, gridKey);

    const items: UserListContextInitialValues = {
        mrt_columns,

        userPage,
        setUserPage,
        totalItems,

        sorting,
        setSorting,

        users,
        isLoading,

        searchUser: search.search,
        delayedSearchUser: search.delayedSearch,
        setSearchUser: search.setSearch,
        resetSearch: search.resetSearch,

        // Modal states
        showCreateModal,
        setShowCreateModal,

        gridKey: gridKey,
    };

    return <UserListContext.Provider value={items}>{children}</UserListContext.Provider>;
};

export const useUserListContext = () => {
    const context = useContext(UserListContext);
    if (context === undefined) {
        throw new Error("useUserListContext must be used within a UserListContextProvider");
    }
    return context;
};
